/* eslint-disable react/no-array-index-key */
import { useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import Tile from '@/components/Tile/Tile'
import Grid from '@/config/Grid/Grid'
import styles from './OrderPage.module.scss'
import { useOrderPageQuery, buildOrderPayload } from './hooks/useOrderPageQuery'
import type { OrderDishModel } from './hooks/useOrderPageStore'
import OrderTile from '@/components/OrderTile/OrderTile'
import {
  primaryColors,
  whiteShades
} from '@/assets/styles/helpers/colors.styles'
import GuestsList from './components/GuestsList/GuestsList'
import IconButton from '@/components/IconButton/IconButton'
import SummaryItemComponent from '@/components/SummaryItemComponent/SummaryItemComponent'
import Button from '@/components/Button/Button'
import useTablesStore from '../TablesList/hooks/useTablesStore'
import useOrderPageStore from './hooks/useOrderPageStore'
import { useDishConfigurationStore } from '../DishConfiguration/hooks/useDishConfigurationStore'
import { useDishConfigurationQuery } from '../DishConfiguration/hooks/useDishConfigurationQuery'

const CATEGORY_PASTEL_COLORS = [
  '#E57373', // soft red (All categories)
  '#81C784', // soft green
  '#64B5F6', // soft blue
  '#FFB74D', // soft orange
  '#BA68C8', // soft purple
  '#4DB6AC', // soft teal
  '#F06292', // soft pink
  '#A1887F', // soft brown
  '#7986CB', // soft indigo
  '#FFD54F', // soft amber
  '#AED581', // soft lime
  '#4DD0E1' // soft cyan
]

function OrderPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tableId: routeTableId } = useParams()
  const { dishCategoriesList, dishesList } = useOrderPageQuery()

  const defaultLanguage = localStorage.getItem('i18nextLng') || 'en'
  const mappedDishCategoriesList = dishCategoriesList?.map(category => ({
    id: category.id,
    title: category.translations.find(t => t.languageCode === defaultLanguage)
      ?.name
  }))

  const mappedDishesList = dishesList?.map(dish => ({
    id: dish.id,
    title: dish.translations.find(t => t.languageCode === defaultLanguage)
      ?.name,
    price: dish.price || '',
    dishCategoryIds: dish.dishCategoryIds
  }))

  const [selectedCategoryTile, setSelectedCategoryTile] = useState(0)
  const setSelectedCategoryTileHandler = (index: number) => {
    setSelectedCategoryTile(index)
  }

  const filteredDishesList = useMemo(() => {
    if (!mappedDishesList) return []
    if (selectedCategoryTile === 0) return mappedDishesList
    const selectedCategory =
      mappedDishCategoriesList?.[selectedCategoryTile - 1]
    if (!selectedCategory?.id) return mappedDishesList
    return mappedDishesList.filter(dish =>
      dish.dishCategoryIds?.includes(selectedCategory.id!)
    )
  }, [mappedDishesList, selectedCategoryTile, mappedDishCategoriesList])

  const {
    selectedGuestsNumber,
    selectedTableNumber,
    setSelectedGuestsNumber,
    selectedTableId
  } = useTablesStore()

  const handleAddSingelGuest = () => {
    setSelectedGuestsNumber(String(Number(selectedGuestsNumber) + 1))
  }

  const { order, resetOrder, removeDishes, setTableIds } = useOrderPageStore()

  useEffect(() => {
    if (routeTableId && !order.tableIds.includes(routeTableId)) {
      setTableIds([routeTableId])
    }
  }, [routeTableId, order.tableIds, setTableIds])

  const {
    guestNumber,
    setDishSizeId,
    setDishToppingIds,
    setNote,
    setGuestNumber,
    reset: resetDishConfig
  } = useDishConfigurationStore()
  const { dishSizesList, toppingsList } = useDishConfigurationQuery()

  const [showTiles, setShowTiles] = useState(false)

  const handleDishOnClick = (dishId: string) => {
    navigate(`/table/${selectedTableId}/new-order/${dishId}`)
  }

  const handleEditDish = (item: (typeof groupedSummaryByGuest)[number]) => {
    resetDishConfig()
    if (item.sizeId) setDishSizeId(item.sizeId)
    if (item.toppingIds.length) setDishToppingIds(item.toppingIds)
    if (item.extraNote) setNote(item.extraNote)
    setGuestNumber(String(guestNumber))
    navigate(`/table/${selectedTableId}/new-order/${item.dishId}`, {
      state: { editIds: item.ids, editCount: item.count }
    })
  }

  const handleCancelOrder = () => {
    resetOrder()
    navigate('/tables-list')
  }

  const mappedDishesByGuest = useMemo(
    () =>
      order.dishes.reduce((acc: Record<string, typeof order.dishes>, dish) => {
        const guestNum = dish.guestNumber || '1'
        if (!acc[guestNum]) {
          acc[guestNum] = []
        }
        acc[guestNum].push(dish)
        return acc
      }, {}),
    [order]
  )

  const groupedSummaryByGuest = useMemo(() => {
    const getDishGroupKey = (dish: OrderDishModel) => {
      const toppingKey = (dish.toppings ?? [])
        .map(t => t.id)
        .sort()
        .join(',')
      return `${dish.dishId}|${dish.sizeId ?? ''}|${toppingKey}|${dish.extraNote ?? ''}`
    }

    const guestDishes = mappedDishesByGuest[guestNumber]
    if (!guestDishes) return []

    const groups: {
      key: string
      ids: string[]
      dish: (typeof guestDishes)[number]
      count: number
    }[] = []

    guestDishes.forEach(dish => {
      const key = getDishGroupKey(dish)
      const existing = groups.find(g => g.key === key)
      if (existing) {
        existing.count += 1
        existing.ids.push(dish.id)
      } else {
        groups.push({ key, ids: [dish.id], dish, count: 1 })
      }
    })

    return groups.map(group => {
      const { dish } = group
      const name = dishesList
        ?.find(d => d.id === dish.dishId)
        ?.translations.find(t => t.languageCode === 'en')?.name
      const description = dishesList
        ?.find(d => d.id === dish.dishId)
        ?.translations.find(t => t.languageCode === 'en')?.description
      const dishSizeName = dish.sizeId
        ? dishSizesList
            ?.find(s => s.id === dish.sizeId)
            ?.translations.find(t => t.languageCode === defaultLanguage)?.name
        : undefined
      const basePrice =
        dishesList
          ?.find(d => d.id === dish.dishId)
          ?.priceToDishSizeId.find(p => p.id === dish.sizeId)?.price ||
        dishesList?.find(d => d.id === dish.dishId)?.price
      const note = dish.extraNote
      const { discount } = dish as { discount?: string }
      const toppings = dish.toppings?.map(topping => {
        const toppingInfo = toppingsList?.find(t => t.id === topping.id)
        const toppingPrice = toppingInfo?.price ?? 0
        return {
          name:
            toppingInfo?.translations.find(
              t => t.languageCode === defaultLanguage
            )?.name ??
            toppingInfo?.translations.find(t => t.languageCode === 'en')
              ?.name ??
            t('ORDER_PAGE.UNKNOWN_TOPPING'),
          price: toppingPrice
            ? t('COMMON.CURRENCY_FORMAT', {
                value: (toppingPrice / 100).toFixed(2)
              })
            : t('COMMON.CURRENCY_FORMAT', { value: '0.00' }),
          rawPrice: toppingPrice
        }
      })

      const toppingsTotal =
        toppings?.reduce((sum, t) => sum + (t.rawPrice ?? 0), 0) ?? 0
      const price = (basePrice ?? 0) + toppingsTotal

      return {
        count: group.count,
        ids: group.ids,
        dishId: dish.dishId,
        sizeId: dish.sizeId,
        toppingIds: (dish.toppings ?? []).map(t => t.id),
        extraNote: dish.extraNote,
        name,
        description,
        dishSize: dishSizeName,
        basePrice,
        price,
        toppings,
        note,
        discount
      }
    })
  }, [
    mappedDishesByGuest,
    guestNumber,
    dishesList,
    dishSizesList,
    toppingsList,
    defaultLanguage,
    t
  ])

  // const getPrintData = () =>
  //   order.dishes.map(dish => {
  //     const dishInfo = dishesList?.find(d => d.id === dish.dishId)
  //     const translation = dishInfo?.translations.find(
  //       t => t.languageCode === defaultLanguage
  //     )

  //     const basePrice =
  //       dishInfo?.priceToDishSizeId.find(p => p.id === dish.sizeId)?.price ||
  //       dishInfo?.price

  //     const sizeName = dish.sizeId
  //       ? dishSizesList
  //           ?.find(s => s.id === dish.sizeId)
  //           ?.translations.find(t => t.languageCode === defaultLanguage)?.name
  //       : undefined

  //     const toppings = dish.toppings?.map(t => {
  //       const toppingInfo = toppingsList?.find(tp => tp.id === t.id)
  //       const toppingPrice = toppingInfo?.price ?? 0
  //       return {
  //         name:
  //           toppingInfo?.translations.find(
  //             tr => tr.languageCode === defaultLanguage
  //           )?.name ??
  //           toppingInfo?.translations.find(tr => tr.languageCode === 'en')
  //             ?.name ??
  //           'Unknown Topping',
  //         price: toppingPrice
  //           ? `${(toppingPrice / 100).toFixed(2)} zł`
  //           : undefined,
  //         rawPrice: toppingPrice
  //       }
  //     })

  //     const toppingsTotal =
  //       toppings?.reduce((sum, t) => sum + (t.rawPrice ?? 0), 0) ?? 0
  //     const totalPrice = (basePrice ?? 0) + toppingsTotal

  //     return {
  //       guestNumber: Number(dish.guestNumber || 1),
  //       count: 1,
  //       name: translation?.name || 'Unknown Dish',
  //       description: translation?.description || '',
  //       dishSize: sizeName,
  //       price: totalPrice ? `${(totalPrice / 100).toFixed(2)} zł` : '0.00 zł',
  //       note: dish.extraNote,
  //       toppings: toppings?.map(({ name, price }) => ({ name, price })),
  //       deletedIngredients: []
  //     }
  //   })

  const subtotal =
    groupedSummaryByGuest?.reduce(
      (sum, item) => sum + (item.price ?? 0) * item.count,
      0
    ) ?? 0
  const tax = subtotal * 0.05
  const total = subtotal + tax

  const formatPrice = (value: number) =>
    t('COMMON.CURRENCY_FORMAT', { value: (value / 100).toFixed(2) })

  const { postOrder } = useOrderPageQuery()

  const handleConfirmOrder = () => {
    const payload = buildOrderPayload(order.tableIds, order.dishes)
    postOrder(payload, {
      onSuccess: () => {
        resetOrder()
        navigate('/tables-list')
      }
    })
  }

  return (
    <>
      <Grid>
        <div className={`start-1 col-7 ${styles.orderPageButtonsTileWrapper}`}>
          <Tile color="black" customPadding="1rem">
            {showTiles ? (
              <>
                <div className={styles.orderPageTilesContainer}>
                  <OrderTile
                    title={t('ORDER_PAGE.ALL_CATEGORIES')}
                    description={t('ORDER_PAGE.ITEMS_COUNT', {
                      count: mappedDishesList?.length ?? 0
                    })}
                    backgroundColor={CATEGORY_PASTEL_COLORS[0]}
                    selected={selectedCategoryTile === 0}
                    onClick={setSelectedCategoryTileHandler}
                    index={0}
                  />
                  {mappedDishCategoriesList?.map((category, index) => (
                    <OrderTile
                      title={category.title || t('ORDER_PAGE.NO_TITLE')}
                      description={t('ORDER_PAGE.ITEMS_COUNT', {
                        count:
                          mappedDishesList?.filter(d =>
                            d.dishCategoryIds?.includes(category.id!)
                          ).length ?? 0
                      })}
                      backgroundColor={
                        CATEGORY_PASTEL_COLORS[
                          (index + 1) % CATEGORY_PASTEL_COLORS.length
                        ]
                      }
                      selected={selectedCategoryTile === index + 1}
                      onClick={setSelectedCategoryTileHandler}
                      index={index + 1}
                    />
                  ))}
                </div>
                <Divider
                  sx={{
                    backgroundColor: primaryColors.white,
                    margin: '0.5rem'
                  }}
                />
                <div className={styles.orderPageTilesContainer}>
                  {filteredDishesList?.map(dish => (
                    <OrderTile
                      key={dish.id}
                      title={dish.title || t('ORDER_PAGE.NO_TITLE')}
                      description={
                        dish.price
                          ? t('COMMON.CURRENCY_FORMAT', {
                              value: (Number(dish.price) / 100).toFixed(2)
                            })
                          : t('ORDER_PAGE.NO_PRICE')
                      }
                      backgroundColor={whiteShades.shade1}
                      onClick={() => handleDishOnClick(dish.id!)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className={`title-bold-medium ${styles.orderPagePlaceholder}`}>
                {t('ORDER_PAGE.PLACEHOLDER')}
              </p>
            )}
          </Tile>
        </div>
        <div className={`start-8 col-5 ${styles.orderPageSummaryTileWrapper}`}>
          <Tile
            color="white"
            height="calc(100% - 6.25rem)"
            customPadding="1.5rem"
          >
            <div className={styles.orderPageSummaryTileContainer}>
              <p className={`title-bold-medium ${styles.orderSummaryHeader}`}>
                {t('ORDER_PAGE.TABLE', { number: selectedTableNumber })}
              </p>
              <div className={styles.orderSummaryGuestsListWrapper}>
                <GuestsList guestsNumber={Number(selectedGuestsNumber)} />
                <IconButton
                  icon="las la-plus icon-large"
                  color="primary"
                  onClick={handleAddSingelGuest}
                />
              </div>
              <div className={styles.orderSummaryItemsWrapper}>
                <div className={styles.orderSummaryItemsContainer}>
                  {groupedSummaryByGuest?.map((summaryItem, index) => (
                    <SummaryItemComponent
                      key={index}
                      count={summaryItem.count}
                      name={summaryItem.name || ''}
                      dishSize={summaryItem.dishSize}
                      price={
                        summaryItem.price
                          ? t('COMMON.CURRENCY_FORMAT', {
                              value: (Number(summaryItem.price) / 100).toFixed(
                                2
                              )
                            })
                          : t('ORDER_PAGE.NO_PRICE')
                      }
                      basePrice={
                        summaryItem.basePrice &&
                        summaryItem.basePrice !== summaryItem.price
                          ? t('COMMON.CURRENCY_FORMAT', {
                              value: (
                                Number(summaryItem.basePrice) / 100
                              ).toFixed(2)
                            })
                          : undefined
                      }
                      toppings={summaryItem.toppings}
                      note={summaryItem.note}
                      discount={summaryItem.discount}
                      onClick={() => handleEditDish(summaryItem)}
                      onDelete={() => {
                        if (summaryItem.ids.length)
                          removeDishes(summaryItem.ids)
                      }}
                    />
                  ))}
                  {showTiles && (
                    <div
                      className={styles.orderSummaryItemComponentPlaceholder}
                    >
                      <i
                        className={`las la-spinner icon-large ${styles.orderSummaryItemComponentPlaceholderIcon}`}
                      />
                      <p className="title-bold-medium">
                        {t('ORDER_PAGE.SUMMARY_ITEM_PLACEHOLDER')}
                      </p>
                    </div>
                  )}
                </div>
                <div className={styles.addItemButtonWrapper}>
                  <Button
                    label={t('ORDER_PAGE.ADD_ITEM')}
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowTiles(true)}
                    mobile
                    maxWidth
                    icon="las la-plus icon-large"
                    iconLocation="right"
                    disabled={showTiles}
                  />
                </div>
              </div>
              <div className={styles.orderSummaryButtonsWrapper}>
                <Button
                  label="Confirm"
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmOrder}
                  mobile
                  maxWidth
                  disabled={order.dishes.length === 0}
                />
                <Button
                  label="Cancel order"
                  variant="contained"
                  color="secondary"
                  onClick={handleCancelOrder}
                  mobile
                  maxWidth
                />
              </div>
            </div>
          </Tile>
          <div className={styles.orderSummaryTotalWrapper}>
            <div className={styles.orderSummaryTotalContainer}>
              <p className={`title-medium ${styles.orderSummaryTotalLabel}`}>
                {t('ORDER_PAGE.SUBTOTAL')}
              </p>
              <p className={`title-medium ${styles.orderSummaryTotalLabel}`}>
                {formatPrice(subtotal)}
              </p>
            </div>
            <div className={styles.orderSummaryTotalContainer}>
              <p className={`title-medium ${styles.orderSummaryTotalLabel}`}>
                {t('ORDER_PAGE.TAX')}
              </p>
              <p className={`title-medium ${styles.orderSummaryTotalLabel}`}>
                {formatPrice(tax)}
              </p>
            </div>
            <div className={styles.orderSummaryTotalContainer}>
              <p
                className={`title-bold-large ${styles.orderSummaryTotalLabel}`}
              >
                {t('ORDER_PAGE.PAYABLE_AMOUNT')}
              </p>
              <p className={`title-large ${styles.orderSummaryTotalLabel}`}>
                {formatPrice(total)}
              </p>
            </div>
          </div>
        </div>
      </Grid>
      {/* <OrderTicket orderId={order.tableIds?.[0] ?? ''} items={getPrintData()} /> */}
    </>
  )
}
export default OrderPage
